import { ILaya } from "../../ILaya";
import { HideFlags, NodeFlags } from "../Const";
import { SpriteConst } from "../display/SpriteConst";
import { Point } from "../maths/Point";
import { Rectangle } from "../maths/Rectangle";
import { PAL } from "../platform/PlatformAdapters";
import { Browser } from "../utils/Browser";
import { Delegate } from "../utils/Delegate";
import { Event } from "./Event";
var _isFirstTouch = true;
const _tempPoint = new Point();
const _tempRect = new Rectangle();
const _rollOverChain = [];
const _rollOutChain = [];
var _inst;
export class InputManager {
    constructor() {
        this._touches = [];
        this._touchPool = [];
        this._mouseTouch = new TouchInfo(this._touches);
        this._pressKeys = new Set();
        this._keyEvent = new Event();
        this._keyEvent._touches = this._touches;
    }
    static get inst() {
        return _inst;
    }
    static getTouchPos(touchId) {
        var _a;
        if (touchId == null)
            touchId = InputManager.lastTouchId;
        return ((_a = _inst.getTouch(touchId)) === null || _a === void 0 ? void 0 : _a.pos) || _inst._mouseTouch.pos;
    }
    static get touchTarget() {
        return _inst._touchTarget;
    }
    static get touches() {
        return _inst._touches;
    }
    static get touchCount() {
        return _inst._touches.length;
    }
    static cancelClick(touchId) {
        if (touchId == null)
            touchId = InputManager.lastTouchId;
        let touch = _inst.getTouch(touchId);
        if (touch)
            touch.clickCancelled = true;
    }
    static hasKeyDown(key) {
        return _inst._pressKeys.has(key);
    }
    static __init__() {
        let inst = _inst = new InputManager();
        inst._stage = ILaya.stage;
        let canvas = Browser.mainCanvas.source;
        let passiveOption = { passive: false };
        canvas.oncontextmenu = () => {
            return false;
        };
        canvas.addEventListener("mousedown", ev => {
            if (!Browser.onIE)
                (ev.cancelable) && (ev.preventDefault());
            inst.handleMouse(ev, 0);
        }, passiveOption);
        canvas.addEventListener("mouseup", ev => {
            (ev.cancelable) && (ev.preventDefault());
            inst.handleMouse(ev, 1);
        }, passiveOption);
        canvas.addEventListener("mousemove", ev => {
            (ev.cancelable) && (ev.preventDefault());
            inst.handleMouse(ev, 2);
        }, passiveOption);
        canvas.addEventListener("mouseout", ev => {
            inst.handleMouse(ev, 3);
        }, passiveOption);
        canvas.addEventListener("touchstart", ev => {
            if (!_isFirstTouch && !PAL.textInput.target)
                (ev.cancelable) && (ev.preventDefault());
            inst.handleTouch(ev, 0);
        }, passiveOption);
        canvas.addEventListener("touchend", ev => {
            if (!_isFirstTouch && !PAL.textInput.target)
                (ev.cancelable) && (ev.preventDefault());
            _isFirstTouch = false;
            inst.handleTouch(ev, 1);
        }, passiveOption);
        canvas.addEventListener("touchmove", ev => {
            (ev.cancelable) && (ev.preventDefault());
            inst.handleTouch(ev, 2);
        }, passiveOption);
        canvas.addEventListener("touchcancel", ev => {
            (ev.cancelable) && (ev.preventDefault());
            inst.handleTouch(ev, 3);
        }, passiveOption);
        canvas.addEventListener("wheel", ev => {
            inst.handleMouse(ev, 4);
        }, passiveOption);
        if (typeof (canvas.setPointerCapture) === 'function') {
            canvas.addEventListener("pointerdown", ev => {
                canvas.setPointerCapture(ev.pointerId);
            });
            canvas.addEventListener("pointerup", ev => {
                canvas.releasePointerCapture(ev.pointerId);
            }, true);
        }
        let doc = Browser.document;
        doc.addEventListener("keydown", ev => {
            inst.handleKeys(ev);
        }, true);
        doc.addEventListener("keypress", ev => {
            inst.handleKeys(ev);
        }, true);
        doc.addEventListener("keyup", ev => {
            inst.handleKeys(ev);
        }, true);
    }
    handleMouse(ev, type) {
        this._eventType = type;
        this._nativeEvent = ev;
        InputManager.lastTouchId = 0;
        let now = Browser.now();
        if (this._lastTouchTime != null && now - this._lastTouchTime < 100)
            return;
        let touch = this._mouseTouch;
        _tempPoint.setTo(ev.pageX || ev.clientX, ev.pageY || ev.clientY);
        this._stage._canvasTransform.invertTransformPoint(_tempPoint);
        InputManager.mouseX = _tempPoint.x;
        InputManager.mouseY = _tempPoint.y;
        let x = _tempPoint.x / this._stage.clientScaleX;
        let y = _tempPoint.y / this._stage.clientScaleY;
        touch.event.nativeEvent = ev;
        if (type == 3 || !InputManager.mouseEventsEnabled)
            touch.target = this._touchTarget = null;
        else {
            touch.target = this._touchTarget = this.getNodeUnderPoint(x, y);
            let ix = Math.round(x);
            let iy = Math.round(y);
            if (ix != touch.pos.x || iy != touch.pos.y) {
                InputManager.lastMouseTime = now;
                touch.pos.setTo(ix, iy);
                touch.move();
                if (InputManager.mouseEventsEnabled) {
                    touch.bubble(Event.MOUSE_MOVE);
                    for (let t of touch.downTargets) {
                        touch.event._stopped = false;
                        t.event(Event.MOUSE_DRAG, touch.event);
                        if (touch.event._stopped)
                            break;
                    }
                }
            }
        }
        if (touch.lastRollOver != touch.target)
            this.handleRollOver(touch);
        if (type == 0) {
            if (!touch.began) {
                touch.begin();
                this._touches[0] = touch;
                touch.event.button = ev.button;
                touch.downButton = ev.button;
                InputManager.onMouseDownCapture.invoke(touch.touchId);
                if (InputManager.mouseEventsEnabled) {
                    if (ev.button == 0)
                        touch.bubble(Event.MOUSE_DOWN);
                    else
                        touch.bubble(Event.RIGHT_MOUSE_DOWN);
                }
            }
        }
        else if (type == 1) {
            if (touch.began && ev.button == touch.downButton) {
                touch.end();
                this._touches.length = 0;
                touch.event.button = ev.button;
                if (InputManager.mouseEventsEnabled) {
                    if (ev.button == 0)
                        touch.bubble(Event.MOUSE_UP);
                    else
                        touch.bubble(Event.RIGHT_MOUSE_UP);
                    if (touch.moved) {
                        for (let t of touch.downTargets)
                            t.event(Event.MOUSE_DRAG_END, touch.event);
                    }
                    let clickTarget = touch.clickTest();
                    if (clickTarget) {
                        if (ev.button == 0) {
                            touch.event.isDblClick = touch.clickCount == 2;
                            touch.bubble(Event.CLICK, clickTarget);
                            if (touch.clickCount == 2)
                                touch.bubble(Event.DOUBLE_CLICK, clickTarget);
                            touch.event.isDblClick = false;
                        }
                        else {
                            touch.event.isDblClick = touch.clickCount == 2;
                            touch.bubble(Event.RIGHT_CLICK, clickTarget);
                            touch.event.isDblClick = false;
                        }
                    }
                }
                touch.event.button = 0;
            }
        }
        else if (type == 4) {
            if (InputManager.mouseEventsEnabled) {
                touch.event.delta = ev.deltaY * 0.025;
                touch.bubble(Event.MOUSE_WHEEL);
                touch.event.delta = 0;
            }
        }
    }
    handleTouch(ev, type) {
        this._eventType = type;
        this._nativeEvent = ev;
        this._lastTouchTime = Browser.now();
        let touches = ev.changedTouches;
        for (let i = 0; i < touches.length; ++i) {
            let uTouch = touches[i];
            if (!InputManager.multiTouchEnabled
                && this._touches.length > 0
                && this._touches[0].touchId != uTouch.identifier)
                continue;
            _tempPoint.setTo(uTouch.pageX, uTouch.pageY);
            this._stage._canvasTransform.invertTransformPoint(_tempPoint);
            InputManager.mouseX = _tempPoint.x;
            InputManager.mouseY = _tempPoint.y;
            let x = _tempPoint.x / this._stage.clientScaleX;
            let y = _tempPoint.y / this._stage.clientScaleY;
            let touch = this.getTouch(uTouch.identifier, type == 0);
            if (!touch)
                continue;
            touch.event.nativeEvent = ev;
            touch.event.touchId = touch.touchId;
            InputManager.lastTouchId = touch.touchId;
            if (type == 3 || !InputManager.mouseEventsEnabled)
                touch.target = this._touchTarget = null;
            else {
                touch.target = this._touchTarget = this.getNodeUnderPoint(x, y);
                InputManager.lastMouseTime = this._lastTouchTime;
                let ix = Math.round(x);
                let iy = Math.round(y);
                if (Math.abs(ix - touch.pos.x) > 1.5 || Math.abs(iy - touch.pos.y) > 1.5) {
                    touch.pos.setTo(ix, iy);
                    if (type == 2) {
                        touch.move();
                        if (InputManager.mouseEventsEnabled) {
                            touch.bubble(Event.MOUSE_MOVE);
                            for (let t of touch.downTargets) {
                                touch.event._stopped = false;
                                t.event(Event.MOUSE_DRAG, touch.event);
                                if (touch.event._stopped)
                                    break;
                            }
                        }
                    }
                }
            }
            if (touch.lastRollOver != touch.target)
                this.handleRollOver(touch);
            if (type == 0) {
                if (!touch.began) {
                    touch.begin();
                    InputManager.onMouseDownCapture.invoke(touch.touchId);
                    if (InputManager.mouseEventsEnabled) {
                        touch.bubble(Event.MOUSE_DOWN);
                    }
                }
            }
            else if (type == 1 || type == 3) {
                if (touch.began) {
                    touch.end();
                    if (InputManager.mouseEventsEnabled) {
                        touch.bubble(Event.MOUSE_UP);
                        if (touch.moved) {
                            for (let t of touch.downTargets)
                                t.event(Event.MOUSE_DRAG_END, touch.event);
                        }
                        if (type != 3) {
                            let clickTarget = touch.clickTest();
                            if (clickTarget != null) {
                                touch.event.isDblClick = touch.clickCount == 2;
                                touch.bubble(Event.CLICK, clickTarget);
                                if (touch.clickCount == 2)
                                    touch.bubble(Event.DOUBLE_CLICK, clickTarget);
                                touch.event.isDblClick = false;
                            }
                        }
                    }
                    touch.target = null;
                    this.handleRollOver(touch);
                }
                touch.reset();
                this._touches.splice(this._touches.indexOf(touch), 1);
                this._touchPool.push(touch);
            }
        }
    }
    getTouch(touchId, shouldCreate) {
        let touch = this._touches.find(e => e.touchId == touchId);
        if (touch || !shouldCreate)
            return touch;
        touch = this._touchPool.length > 0 ? this._touchPool.pop() : new TouchInfo(this._touches);
        touch.touchId = touchId;
        this._touches.push(touch);
        return touch;
    }
    handleKeys(ev) {
        let type = ev.type;
        let keyCode = ev.keyCode;
        if (type === "keydown") {
            if (keyCode != 0)
                this._pressKeys.add(keyCode);
            this._pressKeys.add(ev.key);
        }
        else if (type === "keyup") {
            if (keyCode != 0)
                this._pressKeys.delete(keyCode);
            this._pressKeys.delete(ev.key);
        }
        this._keyEvent.nativeEvent = ev;
        this._keyEvent._defaultPrevented = false;
        if (InputManager.keyEventsEnabled) {
            let target = (this._stage.focus && this._stage.focus.displayedInStage) ? this._stage.focus : this._stage;
            let ct = target;
            while (ct) {
                ct.event(type, this._keyEvent.setTo(type, ct, target));
                ct = ct._parent;
            }
        }
        if (this._keyEvent._defaultPrevented)
            ev.preventDefault();
        this._keyEvent.nativeEvent = null;
    }
    getNodeUnderPoint(x, y) {
        let target = this.getSpriteUnderPoint(this._stage, x, y);
        if (!target)
            target = this.getSprite3DUnderPoint(x, y);
        return target || this._stage;
    }
    getSpriteUnderPoint(sp, x, y) {
        if ((sp._renderType & SpriteConst.AREA2D) !== 0) {
            sp.localToView(x, y, Point.TEMP);
            x = Point.TEMP.x;
            y = Point.TEMP.y;
        }
        let scrollRect = sp._scrollRect;
        if (scrollRect && !sp._getBit(NodeFlags.DISABLE_INNER_CLIPPING)) {
            _tempRect.setTo(scrollRect.x, scrollRect.y, scrollRect.width, scrollRect.height);
            if (!_tempRect.contains(x, y))
                return null;
        }
        let editing = sp._getBit(NodeFlags.EDITING_NODE);
        if (!editing && sp.hitTestPrior && !sp.mouseThrough && sp != this._stage && !this.hitTest(sp, x, y))
            return null;
        for (let i = sp._children.length - 1; i > -1; i--) {
            let child = sp._children[i];
            let childEditing = editing || child._getBit(NodeFlags.EDITING_NODE);
            if (!child._destroyed
                && child._nodeType !== 1
                && (childEditing ? ((!child.hasHideFlag(HideFlags.HideInHierarchy) || child.mouseThrough) && !child._getBit(NodeFlags.HIDE_BY_EDITOR))
                    : (child._mouseState === 2 || child._mouseState === 0 && child._getBit(NodeFlags.CHECK_INPUT)))
                && child._struct.enabled) {
                _tempPoint.setTo(x, y);
                child.fromParentPoint(_tempPoint);
                let ret = this.getSpriteUnderPoint(child, _tempPoint.x, _tempPoint.y);
                if (ret)
                    return ret;
            }
        }
        if (editing) {
            if (!sp._getBit(NodeFlags.LOCK_BY_EDITOR)
                && !sp.hasHideFlag(HideFlags.HideInHierarchy)
                && this.hitTest(sp, x, y, editing))
                return sp;
        }
        else if (sp != this._stage) {
            if (sp.hitTestPrior && !sp.mouseThrough || this.hitTest(sp, x, y))
                return sp;
        }
        return null;
    }
    getSprite3DUnderPoint(x, y) {
        return null;
    }
    hitTest(sp, x, y, editing) {
        let isHit = false;
        if (sp._scrollRect) {
            x -= sp._scrollRect.x;
            y -= sp._scrollRect.y;
        }
        let hitArea = sp._hitArea;
        let mouseThrough = sp.mouseThrough;
        if (editing) {
            hitArea = null;
            mouseThrough = false;
        }
        if (hitArea) {
            return hitArea.contains(x, y, sp);
        }
        if (sp.width > 0 && sp.height > 0 || mouseThrough || hitArea) {
            if (!mouseThrough)
                isHit = (hitArea ? hitArea : _tempRect.setTo(0, 0, sp.width, sp.height)).contains(x, y, sp);
            else
                isHit = sp.getSelfBounds(Rectangle.TEMP, false).contains(x, y);
        }
        return isHit;
    }
    handleRollOver(touch) {
        if (!InputManager.mouseEventsEnabled) {
            touch.lastRollOver = touch.target;
            return;
        }
        _rollOverChain.length = 0;
        _rollOutChain.length = 0;
        let ele = touch.lastRollOver;
        while (ele) {
            _rollOutChain.push(ele);
            ele = ele._parent;
        }
        touch.lastRollOver = touch.target;
        ele = touch.target;
        while (ele) {
            let i = _rollOutChain.indexOf(ele);
            if (i != -1) {
                _rollOutChain.splice(i, _rollOutChain.length - i);
                break;
            }
            _rollOverChain.push(ele);
            ele = ele._parent;
        }
        let cnt = _rollOutChain.length;
        if (cnt > 0) {
            for (let i = 0; i < cnt; i++) {
                ele = _rollOutChain[i];
                if (!ele._destroyed)
                    ele.event(Event.MOUSE_OUT, touch.event.setTo(Event.MOUSE_OUT, ele, ele));
            }
            _rollOutChain.length = 0;
        }
        cnt = _rollOverChain.length;
        if (cnt > 0) {
            for (let i = 0; i < cnt; i++) {
                ele = _rollOverChain[i];
                if (ele.activeInHierarchy)
                    ele.event(Event.MOUSE_OVER, touch.event.setTo(Event.MOUSE_OVER, ele, ele));
            }
            _rollOverChain.length = 0;
        }
        ;
    }
}
InputManager.multiTouchEnabled = true;
InputManager.mouseEventsEnabled = true;
InputManager.keyEventsEnabled = true;
InputManager.clickTestThreshold = 10;
InputManager.mouseX = 0;
InputManager.mouseY = 0;
InputManager.lastMouseTime = 0;
InputManager.lastTouchId = 0;
InputManager.onMouseDownCapture = new Delegate();
const clickTrack = {};
class TouchInfo {
    constructor(touches) {
        this.downPos = new Point();
        this.downTargets = [];
        this.bubbleChain = [];
        this.event = new Event();
        this.event._touches = touches;
        this.pos = this.event.touchPos;
        this.touchId = 0;
        this.reset();
    }
    begin() {
        this.began = true;
        this.clickCancelled = false;
        this.moved = false;
        this.downPos.copy(this.pos);
        this.downTargets.length = 0;
        if (this.target) {
            let ele = this.target;
            while (ele) {
                this.downTargets.push(ele);
                ele = ele._parent;
            }
        }
    }
    move() {
        this.moved = true;
        let ox = Math.abs(this.pos.x - this.downPos.x) * ILaya.stage._canvasTransform.getScaleX();
        let oy = Math.abs(this.pos.y - this.downPos.y) * ILaya.stage._canvasTransform.getScaleY();
        if (ox > InputManager.clickTestThreshold || oy > InputManager.clickTestThreshold)
            this.clickCancelled = true;
    }
    end() {
        this.began = false;
        let now = performance.now();
        let lastClick = clickTrack[this.touchId];
        if (!lastClick) {
            lastClick = { pos: new Point(), time: 0, button: 0 };
            clickTrack[this.touchId] = lastClick;
        }
        if (this.downTargets.length == 0
            || this.clickCancelled
            || Math.abs(this.pos.x - this.downPos.x) > InputManager.clickTestThreshold
            || Math.abs(this.pos.y - this.downPos.y) > InputManager.clickTestThreshold) {
            this.clickCancelled = true;
            lastClick.time = 0;
            this.clickCount = 1;
        }
        else {
            if (now - lastClick.time < 350
                && Math.abs(this.pos.x - lastClick.pos.x) < InputManager.clickTestThreshold
                && Math.abs(this.pos.y - lastClick.pos.y) < InputManager.clickTestThreshold
                && lastClick.button == this.event.button) {
                this.clickCount = 2;
            }
            else
                this.clickCount = 1;
            lastClick.time = now;
            lastClick.pos.copy(this.pos);
            lastClick.button = this.event.button;
        }
    }
    clickTest() {
        if (this.clickCancelled) {
            this.downTargets.length = 0;
            return null;
        }
        let obj = this.downTargets[0];
        if (obj.activeInHierarchy) {
            this.downTargets.length = 0;
            return obj;
        }
        obj = this.target;
        while (obj) {
            let i = this.downTargets.indexOf(obj);
            if (i != -1 && obj.activeInHierarchy)
                break;
            obj = obj._parent;
        }
        this.downTargets.length = 0;
        return obj;
    }
    reset() {
        this.pos.setTo(0, 0);
        this.touchId = 0;
        this.clickCount = 0;
        this.began = false;
        this.moved = false;
        this.target = null;
        this.downTargets.length = 0;
        this.lastRollOver = null;
        this.clickCancelled = false;
        this.downButton = 0;
    }
    bubble(type, target) {
        let arr = this.bubbleChain;
        arr.length = 0;
        target = target || this.target || ILaya.stage;
        let obj = target;
        while (obj) {
            if (obj.activeInHierarchy)
                arr.push(obj);
            obj = obj._parent;
        }
        let evt = this.event;
        evt._stopped = false;
        for (let obj of arr) {
            evt.setTo(type, obj, target);
            obj.event(type, evt);
            if (evt._stopped) {
                if (type === Event.MOUSE_DOWN || type === Event.RIGHT_MOUSE_DOWN) {
                    let i = this.downTargets.indexOf(obj);
                    if (i != -1)
                        this.downTargets.splice(i + 1, this.downTargets.length - i - 1);
                }
                break;
            }
        }
        if (type === Event.MOUSE_UP || type === Event.RIGHT_MOUSE_UP) {
            for (let obj of this.downTargets) {
                if (obj && arr.indexOf(obj) == -1) {
                    evt.setTo(type, obj, target);
                    obj.event(type, evt);
                    if (evt && evt._stopped)
                        break;
                }
            }
        }
    }
}
